// Calendar ICS file generation utility
export interface CalendarEvent {
  id: string;
  title: string;
  description: string;
  location: string;
  startDate: Date;
  endDate: Date;
  status?: 'CONFIRMED' | 'TENTATIVE' | 'CANCELLED';
}

function formatDateToICS(date: Date): string {
  return date.toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';
}

export function generateICS(event: CalendarEvent): Blob {
  const icsContent = `BEGIN:VCALENDAR
VERSION:2.0
PRODID:-//Safeway Roofing//Appointment//EN
CALSCALE:GREGORIAN
METHOD:PUBLISH
BEGIN:VEVENT
UID:${event.id}@safewayroofingandchimney.com
DTSTAMP:${formatDateToICS(new Date())}
DTSTART:${formatDateToICS(event.startDate)}
DTEND:${formatDateToICS(event.endDate)}
SUMMARY:${event.title}
DESCRIPTION:${event.description}
LOCATION:${event.location}
STATUS:${event.status || 'TENTATIVE'}
SEQUENCE:0
END:VEVENT
END:VCALENDAR`;

  return new Blob([icsContent], { type: 'text/calendar;charset=utf-8' });
}

export function downloadICS(blob: Blob, filename: string) {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
}

export function generateGoogleCalendarURL(event: CalendarEvent): string {
  const startDate = formatDateToICS(event.startDate);
  const endDate = formatDateToICS(event.endDate);
  
  const params = new URLSearchParams({
    action: 'TEMPLATE',
    text: event.title,
    dates: `${startDate}/${endDate}`,
    details: event.description,
    location: event.location,
  });
  
  return `https://calendar.google.com/calendar/render?${params.toString()}`;
}

export function generateOutlookCalendarURL(event: CalendarEvent): string {
  const startDate = event.startDate.toISOString();
  const endDate = event.endDate.toISOString();
  
  const params = new URLSearchParams({
    path: '/calendar/action/compose',
    rru: 'addevent',
    subject: event.title,
    body: event.description,
    location: event.location,
    startdt: startDate,
    enddt: endDate,
  });
  
  return `https://outlook.live.com/calendar/0/deeplink/compose?${params.toString()}`;
}

export function generateAppleCalendarURL(blob: Blob): string {
  const url = window.URL.createObjectURL(blob);
  return url.replace('blob:', 'webcal:');
}

export function getTimeSlotDisplay(slot: string): string {
  switch (slot) {
    case 'morning':
      return '8:00 AM - 12:00 PM';
    case 'afternoon':
      return '12:00 PM - 4:00 PM';
    case 'evening':
      return '4:00 PM - 7:00 PM';
    default:
      return slot;
  }
}

export function getTimeSlotStartTime(date: Date, slot: string): Date {
  const newDate = new Date(date);
  switch (slot) {
    case 'morning':
      newDate.setHours(8, 0, 0, 0);
      break;
    case 'afternoon':
      newDate.setHours(12, 0, 0, 0);
      break;
    case 'evening':
      newDate.setHours(16, 0, 0, 0);
      break;
  }
  return newDate;
}

export function getTimeSlotEndTime(date: Date, slot: string): Date {
  const newDate = new Date(date);
  switch (slot) {
    case 'morning':
      newDate.setHours(12, 0, 0, 0);
      break;
    case 'afternoon':
      newDate.setHours(16, 0, 0, 0);
      break;
    case 'evening':
      newDate.setHours(19, 0, 0, 0);
      break;
  }
  return newDate;
}
